import { useRef } from 'react'
import { gql, DocumentNode, OperationVariables } from '@apollo/client'
import isEqual from 'fast-deep-equal'
import {
  FieldNode,
  VariableDefinitionNode,
  NamedTypeNode,
  visit,
  Kind,
} from 'graphql'
import type { GraphQLQueryInfo, GraphQLFieldTree } from './types'
import type {
  DocumentNode as GqlDocumentNode,
  OperationDefinitionNode,
} from 'graphql'

/**
 * A memoization hook that uses deep equality comparison instead of reference equality.
 * This is useful for object and array dependencies that should be considered stable
 * when their content is the same, even if they're different object references.
 *
 * This replaces the need for JSON.stringify in dependency arrays, which is:
 * - Slower (string serialization on every render)
 * - Less reliable (ignores key order, doesn't handle functions/dates/etc.)
 * - Creates unnecessary strings in memory
 *
 * @param value - The value to memoize with deep comparison
 * @returns A stable reference that only changes when the deep content changes
 */
export function useDeepCompareMemo<T>(value: T): T {
  const ref = useRef<T>(value)

  if (!isEqual(ref.current, value)) {
    ref.current = value
  }

  return ref.current
}

/**
 * Converts an array of dot-notation field paths into a GraphQL query structure.
 *
 * @example
 * buildFieldsFromDotNotation(['user.name', 'user.email', 'id'])
 * // Returns: "id\nuser {\n  name\n  email\n}"
 */
export const buildFieldsFromDotNotation = (fields: string[]): string => {
  if (!Array.isArray(fields) || fields.length === 0) {
    return ''
  }

  const tree: GraphQLFieldTree = new Map()

  for (const path of fields) {
    const parts = path.split('.')
    let current = tree

    parts.forEach((part, index) => {
      if (!current.has(part)) {
        current.set(part, index === parts.length - 1 ? true : new Map())
      }
      if (index < parts.length - 1) {
        current = current.get(part) as GraphQLFieldTree
      }
    })
  }

  const buildQueryString = (map: GraphQLFieldTree, indent = ''): string =>
    Array.from(map.entries())
      .map(([key, value]) =>
        value === true
          ? `${indent}${key}`
          : `${indent}${key} {\n${buildQueryString(
              value,
              indent + '  ',
            )}\n${indent}}`,
      )
      .join('\n')

  return buildQueryString(tree)
}

/**
 * Builds a complete GraphQL query with variables for resource fetching.
 *
 * @param params - Query parameters including resource name, object UID, fragment, fields, and variables
 * @returns Complete query information including DocumentNode, query name, and final variables
 */
export const buildGraphQLQuery = ({
  resourceName,
  objectUid,
  fragment,
  fields,
  variables = {},
}: {
  resourceName: string
  objectUid: string
  fragment?: string
  fields?: string[]
  variables?: OperationVariables
}): GraphQLQueryInfo => {
  // Input validation
  if (
    !resourceName ||
    typeof resourceName !== 'string' ||
    resourceName.trim().length === 0
  ) {
    throw new Error('resourceName is required and must be a non-empty string')
  }

  if (
    !objectUid ||
    typeof objectUid !== 'string' ||
    objectUid.trim().length === 0
  ) {
    throw new Error('objectUid is required and must be a non-empty string')
  }

  // Sanitize resource name to ensure it's a valid GraphQL identifier
  const sanitizedResourceName = resourceName
    .trim()
    .replace(/[^a-zA-Z0-9_]/g, '')

  if (sanitizedResourceName.length === 0) {
    throw new Error(
      'resourceName must contain valid GraphQL identifier characters',
    )
  }

  const queryName = `${sanitizedResourceName}ById`
  if (fragment && typeof fragment !== 'string') {
    throw new Error(
      'buildGraphQLQuery: fragment must be a string of field selections',
    )
  }

  const queryFields = fragment || buildFieldsFromDotNotation(fields || ['UID'])

  const query = gql`
    query ${queryName}($id: ID!) {
      ${queryName}(UID: $id) {
        ${queryFields}
      }
    }
  `

  return {
    query,
    queryName,
    finalVariables: { id: objectUid, ...variables },
  }
}

/**
 * Formats Apollo GraphQL errors into a human-readable string with detailed information.
 *
 * @param error - Apollo error object
 * @returns Formatted error message with GraphQL errors, network errors, and context
 */
// Error helpers moved to errors.ts

/**
 * Simple refetch query generation from basic configuration.
 * Can auto-detect fields from mutation variables if not provided.
 */
export const generateSimpleRefetchQuery = (
  resourceName: string,
  entityId: string,
  fields: string[],
): { query: DocumentNode; variables: OperationVariables } => {
  // Generate the query with the provided fields
  const query = gql`
    query AutoRefetch${capitalize(resourceName)}($id: ID!) {
      ${resourceName}ById(UID: $id) {
        ${fields.join('\n          ')}
      }
    }
  `

  return {
    query,
    variables: { id: entityId },
  }
}

/**
 * Auto-detect fields from mutation variables.
 * Extracts field names from variables.input object.
 */
export const extractFieldsFromVariables = (
  variables?: OperationVariables,
): string[] => {
  if (!variables?.input || typeof variables.input !== 'object') {
    return ['UID'] // Fallback to just UID if no input found
  }

  // Get all keys from the input object
  const inputFields = Object.keys(variables.input)

  // Always include UID if it's not already there
  const fields = inputFields.includes('UID')
    ? inputFields
    : ['UID', ...inputFields]

  return fields
}

/**
 * Capitalize first letter of a string
 */
const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Extract entity ID from mutation variables (supports common patterns)
 */
export const extractEntityIdFromVariables = (
  variables?: OperationVariables,
): string | null => {
  if (!variables) return null

  // Pattern 1: variables.input.UID (most common)
  if (variables.input?.UID) return variables.input.UID

  // Pattern 2: variables.input.uid
  if (variables.input?.uid) return variables.input.uid

  // Pattern 3: variables.input.id
  if (variables.input?.id) return variables.input.id

  // Pattern 4: Direct UID
  if (variables.UID) return variables.UID

  // Pattern 5: Direct uid
  if (variables.uid) return variables.uid

  // Pattern 6: Direct id
  if (variables.id) return variables.id

  return null
}

/**
 * Creates an aggregated mutation that includes both the original mutation
 * and a get operation to fetch fresh data in a single network request.
 *
 * @example
 * Original mutation:
 * mutation UpdateJob($input: UpdateJobs!) {
 *   schema { updateJobs(input: $input) }
 * }
 *
 * Becomes:
 * mutation UpdateJob($input: UpdateJobs!, $UID: ID!) {
 *   schema {
 *     updateJobs(input: $input)
 *     getJobs(id: $UID) {
 *       UID
 *       Description
 *     }
 *   }
 * }
 */
export const createAggregatedMutation = (
  originalMutation: DocumentNode,
  resourceName: string,
  entityId: string,
  fields: string[],
): { mutation: DocumentNode; variables: OperationVariables } => {
  // Extract the original operation definition
  const originalOperation = originalMutation.definitions.find(
    (def): def is OperationDefinitionNode =>
      def.kind === 'OperationDefinition' && def.operation === 'mutation',
  )

  if (!originalOperation) {
    throw new Error(
      'Invalid mutation DocumentNode: no mutation operation found',
    )
  }

  // Create the get operation field name for mutation schema
  // e.g., "jobs" -> "getJobs" (mutation operation, not query)
  const getOperationName = `get${capitalize(resourceName)}`

  // Create the get operation field
  const getField: FieldNode = {
    kind: Kind.FIELD,
    name: {
      kind: Kind.NAME,
      value: getOperationName,
    },
    arguments: [
      {
        kind: Kind.ARGUMENT,
        name: {
          kind: Kind.NAME,
          value: 'id',
        },
        value: {
          kind: Kind.VARIABLE,
          name: {
            kind: Kind.NAME,
            value: 'UID',
          },
        },
      },
    ],
    selectionSet: {
      kind: Kind.SELECTION_SET,
      selections: fields.map((field) => ({
        kind: Kind.FIELD,
        name: {
          kind: Kind.NAME,
          value: field,
        },
      })),
    },
  }

  // Create the $UID variable definition
  const uidVariableDefinition: VariableDefinitionNode = {
    kind: Kind.VARIABLE_DEFINITION,
    variable: {
      kind: Kind.VARIABLE,
      name: {
        kind: Kind.NAME,
        value: 'UID',
      },
    },
    type: {
      kind: Kind.NON_NULL_TYPE,
      type: {
        kind: Kind.NAMED_TYPE,
        name: {
          kind: Kind.NAME,
          value: 'ID',
        },
      } as NamedTypeNode,
    },
  }

  // Clone and modify the mutation
  const modifiedMutation = visit(originalMutation, {
    OperationDefinition(node) {
      if (node.operation === 'mutation') {
        // Add the $UID variable definition
        const existingVariables = node.variableDefinitions || []
        const hasUidVariable = existingVariables.some(
          (varDef) => varDef.variable.name.value === 'UID',
        )

        const variableDefinitions = hasUidVariable
          ? existingVariables
          : [...existingVariables, uidVariableDefinition]

        // Add the get operation to the schema field
        return {
          ...node,
          variableDefinitions,
          selectionSet: {
            ...node.selectionSet,
            selections: node.selectionSet.selections.map((selection) => {
              if (
                selection.kind === 'Field' &&
                selection.name.value === 'schema' &&
                selection.selectionSet
              ) {
                // Add the get operation to the schema field
                return {
                  ...selection,
                  selectionSet: {
                    ...selection.selectionSet,
                    selections: [
                      ...selection.selectionSet.selections,
                      getField,
                    ],
                  },
                }
              }
              return selection
            }),
          },
        }
      }
      return node
    },
  })

  return {
    mutation: modifiedMutation,
    variables: { UID: entityId },
  }
}

/**
 * Selection sets under any of these fields hold aggregation result types
 * (e.g. `ShiftsSumFields`, `ShiftsSumByResult`) rather than entity types,
 * so they don't expose a `UID` field. Injecting `UID` would 400 the query.
 *
 * Covers the operations rolled out in COR-83 — `count`/`sum`/`avg`/`min`/`max`
 * and their grouped `*By` variants. `groupBy` lives inside the `*By` variants
 * and is also an aggregation result shape, so it's included for completeness;
 * in practice the ancestor check below already covers it.
 */
const AGGREGATION_FIELD_NAMES = new Set([
  'count',
  'countBy',
  'sum',
  'sumBy',
  'avg',
  'avgBy',
  'min',
  'minBy',
  'max',
  'maxBy',
  'groupBy',
])

/**
 * Automatically injects UID field into GraphQL queries for proper cache normalization.
 * This ensures Apollo Client can create consistent cache keys even if the developer
 * forgets to include UID in their query fields.
 *
 * Aggregation selection sets are left untouched — their result types are not
 * entities and do not declare a `UID` field, so combining a normal query with
 * an aggregation block (e.g. `shifts { edges { node { ... } } sum { Duration } }`)
 * still works.
 *
 * @param query - The original DocumentNode query
 * @returns Modified DocumentNode with UID field injected if missing
 */
export const ensureUIDInQuery = (query: DocumentNode): DocumentNode => {
  return visit(query, {
    SelectionSet(node, _key, parent, _path, ancestors) {
      // Only process selection sets that are directly on field nodes (not fragments)
      if (parent && 'kind' in parent && parent.kind === Kind.FIELD) {
        // Skip selection sets that live inside an aggregation operation —
        // the result types don't have a `UID` field. Check the immediate
        // parent and any enclosing field; this catches both `sum { ... }`
        // and the nested `groupBy { ResourceShifts { ... } }`.
        const parentField = parent as FieldNode
        const parentIsAggregation = AGGREGATION_FIELD_NAMES.has(
          parentField.name.value,
        )
        const ancestorIsAggregation = ancestors.some(
          (a) =>
            a !== null &&
            typeof a === 'object' &&
            !Array.isArray(a) &&
            'kind' in a &&
            a.kind === Kind.FIELD &&
            AGGREGATION_FIELD_NAMES.has((a as FieldNode).name.value),
        )
        if (parentIsAggregation || ancestorIsAggregation) {
          return undefined
        }

        const selections = node.selections

        // Skip connection- and meta-like selections by detecting absence of PascalCase fields
        // We only inject UID when the selection set appears to represent an entity shape
        const hasPascalCaseField = selections.some(
          (selection) =>
            selection.kind === Kind.FIELD &&
            /^[A-Z]/.test(selection.name.value || ''),
        )

        if (!hasPascalCaseField) {
          return undefined
        }

        // Check if UID field is already present
        const hasUID = selections.some(
          (selection) =>
            selection.kind === Kind.FIELD && selection.name.value === 'UID',
        )

        // If UID is missing, add it at the beginning
        if (!hasUID) {
          const uidField = {
            kind: Kind.FIELD,
            name: {
              kind: Kind.NAME,
              value: 'UID',
            },
          }

          return {
            ...node,
            selections: [uidField, ...selections],
          }
        }
      }

      // Return unchanged if UID already exists or not applicable
      return undefined
    },
  })
}

/**
 * Extract operation type and name from a GraphQL DocumentNode.
 * Returns undefined properties if not found.
 */
export const extractOperationInfo = (
  doc: GqlDocumentNode,
): { operation?: 'query' | 'mutation' | 'subscription'; name?: string } => {
  try {
    const op = doc.definitions.find(
      (def): def is OperationDefinitionNode =>
        (def as { kind?: string }).kind === 'OperationDefinition',
    )
    return {
      operation: op?.operation,
      name: op?.name?.value,
    }
  } catch {
    return {}
  }
}
